import { useState, useEffect } from 'react';
import { Modal, ModalFooter } from '../../ui/Modal';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Textarea } from '../../ui/Textarea';
import { Button } from '../../ui/Button';
import { TimePicker } from '../../ui/TimePicker';
import type { SavedMeeting, MeetingFormat, MeetingType, MeetingFormatType } from '../../../types/meetings';
import { recoveryPrograms } from '../../../content/meeting-programs/programs';
import type { CustomProgram } from '../../../stores/onboardingStore';

const DAYS_OF_WEEK = [
  { id: 'monday', label: 'Mon' },
  { id: 'tuesday', label: 'Tue' },
  { id: 'wednesday', label: 'Wed' },
  { id: 'thursday', label: 'Thu' },
  { id: 'friday', label: 'Fri' },
  { id: 'saturday', label: 'Sat' },
  { id: 'sunday', label: 'Sun' },
];

interface MeetingFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Omit<SavedMeeting, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  initialData?: SavedMeeting;
  selectedPrograms: string[];
  customPrograms: CustomProgram[];
}

type FormState = {
  name: string;
  fellowship: string;
  fellowshipName: string;
  isHomeGroup: boolean;
  format: MeetingFormat;
  dayOfWeek: string[];
  time: string;
  address: string;
  city: string;
  zoomLink: string;
  zoomMeetingId: string;
  zoomPasscode: string;
  meetingType: MeetingType | '';
  meetingFormat: MeetingFormatType | '';
  notes: string;
};

const EMPTY_FORM: FormState = {
  name: '',
  fellowship: '',
  fellowshipName: '',
  isHomeGroup: false,
  format: 'in-person',
  dayOfWeek: [],
  time: '19:00',
  address: '',
  city: '',
  zoomLink: '',
  zoomMeetingId: '',
  zoomPasscode: '',
  meetingType: '',
  meetingFormat: '',
  notes: '',
};

export function MeetingForm({
  isOpen,
  onClose,
  onSave,
  initialData,
  selectedPrograms,
  customPrograms,
}: MeetingFormProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  // Build fellowship options from user's selected programs + custom programs
  const fellowshipOptions = [
    ...recoveryPrograms
      .filter(p => selectedPrograms.includes(p.id))
      .map(p => ({ value: p.id, label: `${p.shortName} — ${p.name}` })),
    ...customPrograms
      .filter(cp => selectedPrograms.includes(cp.id))
      .map(cp => ({ value: cp.id, label: `${cp.abbreviation} — ${cp.name}` })),
  ];

  // Populate form when editing or reset when opening blank
  useEffect(() => {
    if (!isOpen) return;
    if (initialData) {
      setForm({
        name: initialData.name,
        fellowship: initialData.fellowship,
        fellowshipName: initialData.fellowshipName,
        isHomeGroup: initialData.isHomeGroup,
        format: initialData.format,
        dayOfWeek: initialData.dayOfWeek,
        time: initialData.time,
        address: initialData.address ?? '',
        city: initialData.city ?? '',
        zoomLink: initialData.zoomLink ?? '',
        zoomMeetingId: initialData.zoomMeetingId ?? '',
        zoomPasscode: initialData.zoomPasscode ?? '',
        meetingType: initialData.meetingType ?? '',
        meetingFormat: initialData.meetingFormat ?? '',
        notes: initialData.notes ?? '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors({});
  }, [isOpen, initialData]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  }

  function toggleDay(day: string) {
    setForm(prev => ({
      ...prev,
      dayOfWeek: prev.dayOfWeek.includes(day)
        ? prev.dayOfWeek.filter(d => d !== day)
        : [...prev.dayOfWeek, day],
    }));
  }

  function resolveFellowshipName(fellowshipId: string): string {
    const program = recoveryPrograms.find(p => p.id === fellowshipId);
    if (program) return program.shortName;
    const custom = customPrograms.find(cp => cp.id === fellowshipId);
    if (custom) return custom.abbreviation;
    return fellowshipId;
  }

  function validate(): boolean {
    const newErrors: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) newErrors.name = 'Meeting name is required';
    if (!form.fellowship) newErrors.fellowship = 'Fellowship is required';
    if (form.dayOfWeek.length === 0) newErrors.dayOfWeek = 'Select at least one day';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    setSaving(true);
    try {
      const fellowshipName = resolveFellowshipName(form.fellowship);
      const payload: Omit<SavedMeeting, 'id' | 'createdAt' | 'updatedAt'> = {
        name: form.name.trim(),
        fellowship: form.fellowship,
        fellowshipName,
        isHomeGroup: form.isHomeGroup,
        format: form.format,
        dayOfWeek: form.dayOfWeek,
        time: form.time,
        ...(form.format !== 'online' && form.address ? { address: form.address } : {}),
        ...(form.format !== 'online' && form.city ? { city: form.city } : {}),
        ...(form.format !== 'in-person' && form.zoomLink ? { zoomLink: form.zoomLink } : {}),
        ...(form.format !== 'in-person' && form.zoomMeetingId ? { zoomMeetingId: form.zoomMeetingId } : {}),
        ...(form.format !== 'in-person' && form.zoomPasscode ? { zoomPasscode: form.zoomPasscode } : {}),
        ...(form.meetingType ? { meetingType: form.meetingType as MeetingType } : {}),
        ...(form.meetingFormat ? { meetingFormat: form.meetingFormat as MeetingFormatType } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      };
      await onSave(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const showInPerson = form.format === 'in-person' || form.format === 'hybrid';
  const showOnline = form.format === 'online' || form.format === 'hybrid';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Edit Meeting' : 'Add Meeting'}
      size="lg"
      closeOnOverlayClick={false}
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        {/* Name */}
        <Input
          label="Meeting name"
          placeholder="Monday Night Men's Group"
          value={form.name}
          onChange={e => set('name', e.target.value)}
          error={errors.name}
        />

        {/* Fellowship */}
        <Select
          label="Fellowship"
          value={form.fellowship}
          onChange={e => set('fellowship', e.target.value)}
          options={fellowshipOptions}
          placeholder="Select fellowship…"
          error={errors.fellowship}
        />

        {/* Home Group toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-surface-900 dark:text-surface-100">Home Group</p>
            <p className="text-xs text-surface-500 dark:text-surface-400">Mark as your primary home group</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={form.isHomeGroup}
              onChange={e => set('isHomeGroup', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-300 rounded-full peer dark:bg-surface-700 peer-checked:bg-teal-600 dark:peer-checked:bg-teal-400 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
          </label>
        </div>

        {/* Format */}
        <div>
          <p className="text-sm font-medium text-surface-900 dark:text-surface-100 mb-2">Format</p>
          <div className="grid grid-cols-3 gap-2">
            {(['in-person', 'online', 'hybrid'] as MeetingFormat[]).map(fmt => (
              <button
                key={fmt}
                type="button"
                onClick={() => set('format', fmt)}
                className={`
                  px-3 py-2 rounded-lg text-sm font-medium transition-colors capitalize
                  ${form.format === fmt
                    ? 'bg-teal-600 text-white dark:bg-teal-500'
                    : 'bg-surface-100 text-surface-700 dark:bg-surface-700 dark:text-surface-200 hover:bg-surface-200 dark:hover:bg-surface-600'
                  }
                `}
              >
                {fmt}
              </button>
            ))}
          </div>
        </div>

        {/* In-person fields */}
        {showInPerson && (
          <div className="space-y-3 p-3 rounded-xl bg-surface-50 dark:bg-surface-700/50">
            <p className="text-xs font-semibold text-surface-500 dark:text-surface-400 uppercase tracking-wide">In-person details</p>
            <Input label="Address" placeholder="123 Main St" value={form.address} onChange={e => set('address', e.target.value)} size="sm" />
            <Input label="City" placeholder="City, State" value={form.city} onChange={e => set('city', e.target.value)} size="sm" />
          </div>
        )}

        {/* Online fields */}
        {showOnline && (
          <div className="space-y-3 p-3 rounded-xl bg-surface-50 dark:bg-surface-700/50">
            <p className="text-xs font-semibold text-surface-500 dark:text-surface-400 uppercase tracking-wide">Online details</p>
            <Input label="Zoom link" placeholder="https://zoom.us/j/..." value={form.zoomLink} onChange={e => set('zoomLink', e.target.value)} size="sm" />
            <div className="grid grid-cols-2 gap-2">
              <Input label="Meeting ID" placeholder="123 456 7890" value={form.zoomMeetingId} onChange={e => set('zoomMeetingId', e.target.value)} size="sm" />
              <Input label="Passcode" placeholder="optional" value={form.zoomPasscode} onChange={e => set('zoomPasscode', e.target.value)} size="sm" />
            </div>
          </div>
        )}

        {/* Days of week */}
        <div>
          <p className="text-sm font-medium text-surface-900 dark:text-surface-100 mb-2">Day(s) of week</p>
          <div className="flex gap-1.5 flex-wrap">
            {DAYS_OF_WEEK.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => toggleDay(id)}
                className={`
                  px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                  ${form.dayOfWeek.includes(id)
                    ? 'bg-teal-600 text-white dark:bg-teal-500'
                    : 'bg-surface-100 text-surface-700 dark:bg-surface-700 dark:text-surface-200 hover:bg-surface-200 dark:hover:bg-surface-600'
                  }
                `}
              >
                {label}
              </button>
            ))}
          </div>
          {errors.dayOfWeek && (
            <p className="mt-1 text-xs text-danger-600 dark:text-danger-400">{errors.dayOfWeek}</p>
          )}
        </div>

        {/* Time */}
        <div>
          <p className="text-sm font-medium text-surface-900 dark:text-surface-100 mb-2">Start time</p>
          <TimePicker value={form.time} onChange={v => set('time', v)} />
        </div>

        {/* Meeting type + format */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Meeting type"
            value={form.meetingType}
            onChange={e => set('meetingType', e.target.value as MeetingType | '')}
            options={[
              { value: 'open', label: 'Open' },
              { value: 'closed', label: 'Closed' },
            ]}
            placeholder="Optional"
            size="sm"
          />
          <Select
            label="Meeting format"
            value={form.meetingFormat}
            onChange={e => set('meetingFormat', e.target.value as MeetingFormatType | '')}
            options={[
              { value: 'speaker', label: 'Speaker' },
              { value: 'discussion', label: 'Discussion' },
              { value: 'book_study', label: 'Book Study' },
              { value: 'step_study', label: 'Step Study' },
              { value: 'beginner', label: 'Beginner' },
            ]}
            placeholder="Optional"
            size="sm"
          />
        </div>

        {/* Notes */}
        <Textarea
          label="Notes (standing notes or reminders)"
          placeholder="Bring coffee…"
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          rows={3}
        />
      </div>

      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} isLoading={saving}>
          {initialData ? 'Save Changes' : 'Add Meeting'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
